<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { routes } from '@app/router/routes'
import { useDashboardState } from '@modules/dashboard'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { APP_PRIMARY_NAV, APP_UTILITY_NAV, GUEST_HIDDEN_NAV_ROUTES } from './appNavItems'
import { config } from '@core/config/config'
import logo from '@assets/images/logo.svg'
import slicealText from '@assets/images/sliceal-text.svg'

// Build-time version from package.json (vite `define`), not the design mock's
// literal "v5.1" — a hardcoded string here would drift on the next release.
const versionLabel = `Sliceal - v${config.app.version}`

// Label + icon colour on the filled violet active pill. NOT `inverse`: that
// resolves to `--p-surface-0`, which flips to near-black in dark mode while the
// pill stays violet. The contrast token is white in both themes.
const ON_PRIMARY_COLOR = 'var(--p-primary-contrast-color, #ffffff)'

// Guests have no wallet and no holdings — same rule as the mobile drawer.
const dashboardState = useDashboardState()
const primaryNav = computed(() =>
  dashboardState.value.kind === 'guest'
    ? APP_PRIMARY_NAV.filter((item) => !GUEST_HIDDEN_NAV_ROUTES.includes(item.to))
    : APP_PRIMARY_NAV,
)
</script>

<template>
  <Box is="aside" direction="col" class="sidenav" aria-label="Primary">
    <RouterLink :to="routes.DASHBOARD" class="sidenav__brand" aria-label="Sliceal home">
      <img :src="logo" width="44" height="44" alt="" class="sidenav__brand-icon" />
      <img :src="slicealText" alt="Sliceal" class="sidenav__brand-wordmark" />
    </RouterLink>

    <Box direction="col" is="nav" class="sidenav__nav" aria-label="Sections">
      <RouterLink
        v-for="item in primaryNav"
        :key="item.to"
        v-slot="{ isActive }"
        :to="item.to"
        class="sidenav__link"
        active-class="sidenav__link--active"
      >
        <component :is="item.svg" v-if="item.svg" class="sidenav__icon sidenav__icon--svg" />
        <PiIcon v-else :name="item.icon" class="sidenav__icon" />
        <!-- `<Text>` sets its own colour, so it can't inherit the active
             link's white — it has to be passed explicitly. -->
        <Text
          type="title-lg"
          weight="regular"
          is="span"
          :color="isActive ? ON_PRIMARY_COLOR : 'muted'"
          class="sidenav__label"
        >
          {{ item.label }}
        </Text>
      </RouterLink>
    </Box>

    <!-- Design separates the account links from the sections with a rule and
         keeps them directly beneath it — they don't float to the bottom. -->
    <Box class="sidenav__divider" />

    <Box direction="col" is="nav" class="sidenav__nav" aria-label="Account">
      <RouterLink
        v-for="item in APP_UTILITY_NAV"
        :key="item.to"
        v-slot="{ isActive }"
        :to="item.to"
        class="sidenav__link"
        active-class="sidenav__link--active"
      >
        <PiIcon :name="item.icon" class="sidenav__icon" />
        <Text
          type="title-lg"
          weight="regular"
          is="span"
          :color="isActive ? ON_PRIMARY_COLOR : 'muted'"
          class="sidenav__label"
        >
          {{ item.label }}
        </Text>
      </RouterLink>
    </Box>

    <Box class="sidenav__spacer" />

    <Box align="center" justify="center" class="sidenav__version">
      <Text type="body" weight="semibold" color="muted" is="span">{{ versionLabel }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
/* Dimensions from Figma "Side Menu - NEW" (32590:74310): 268px rail, 80px
   brand row, 16px gutter, 48px rows with 10px gaps, 42px version footer. */
.sidenav {
  width: 16.75rem;
  flex-shrink: 0;
  height: 100%;
  padding: 0;
  background: var(--p-surface-0);
  border-right: 1px solid var(--p-sliceal-gray-200);
  box-sizing: border-box;
  overflow: hidden;
}

/* Brand sits in its own banded row, aligned with the top bar's height so the
   rule under it continues the top bar's border across the full window. The
   mark's SVG carries ~6px of transparent side padding at 44px, so both the
   inset and the gap are trimmed by that much to land the visible mark at
   Figma's 24px inset with a 16px gap to the wordmark (19368:180888). */
.sidenav__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  height: 80px;
  padding: 0 1.5rem 0 18px;
  text-decoration: none;
  border-bottom: 1px solid var(--p-sliceal-gray-200);
  box-sizing: border-box;
}

.sidenav__brand:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.sidenav__brand-icon {
  display: block;
  flex-shrink: 0;
}

.sidenav__brand-wordmark {
  height: 32px;
  width: auto;
  display: block;
}

.sidenav__nav {
  width: 100%;
  flex-shrink: 0;
  gap: 10px;
  padding: 0 1rem;
  box-sizing: border-box;
}

.sidenav__nav:first-of-type {
  padding-top: 42px;
}

.sidenav__divider {
  flex-shrink: 0;
  height: 1px;
  background: var(--p-sliceal-gray-200);
  margin: 14px 1rem;
}

.sidenav__spacer {
  flex: 1;
  min-height: 1.5rem;
}

.sidenav__version {
  flex-shrink: 0;
  height: 42px;
  padding: 0 1.25rem;
  border-top: 1px solid var(--p-sliceal-gray-200);
  box-sizing: border-box;
}

.sidenav__link {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 48px;
  padding: 0 1rem;
  border-radius: 12px;
  text-decoration: none;
  color: var(--p-text-muted-color);
  box-sizing: border-box;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.sidenav__icon {
  font-size: 1.125rem;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
  color: inherit;
}

.sidenav__icon--svg {
  height: 20px;
}

.sidenav__label {
  line-height: 30px;
}

.sidenav__link:hover {
  background: var(--p-sliceal-gray-100);
}

/* Drives the icon, which inherits. The label's colour is passed as a prop —
   `<Text>` sets its own and would otherwise stay dark on the violet pill. */
.sidenav__link--active {
  background: var(--p-primary-600);
  color: var(--p-primary-contrast-color, #ffffff);
}

.sidenav__link--active:hover {
  background: var(--p-primary-700);
  color: var(--p-primary-contrast-color, #ffffff);
}

.sidenav__link:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

@media (max-width: 991px) {
  .sidenav {
    display: none;
  }
}
</style>
